import { router } from 'expo-router';
import { useEffect, useMemo, useRef } from 'react';

import { openCitationReviewWizard } from './openCitationReviewWizard';
import { useCitationRequests } from './useCitationRequests';
import { useStatementReferenceState } from './useStatementReferences';
import { type CitationRequest, isReviewableStatus } from '../model/citationRequest';

import {
  type ReferencesReadyDeepLinkSource,
  trackReferencesReadyDeepLinkOpened,
} from '@/util/analyticsEvents';

/**
 * Search-param value emitted by the `referencesReady` notification.
 * Kept in sync with the backend-side `action` query string; update
 * both sides together if the value changes.
 */
export const REFERENCES_READY_ACTION = 'referencesReady';

const REFERENCES_READY_SOURCES = ['email', 'sms', 'push'] as const;

/**
 * Select the citation request a `referencesReady` deep link should open
 * to review. The link only carries the conversation id (not a message
 * sid), so when several requests exist we prefer the freshly-ready
 * `awaiting_review` request — the state that fires the notification —
 * and otherwise fall back to the most recent reviewable request.
 * `published` counts as reviewable (see `REVIEWABLE_CITATION_STATUSES`):
 * publish is a non-destructive sync the expert can keep refining, and a
 * notification tap that lands after the references were already
 * published should still open the review surface rather than no-op.
 */
function pickReferencesReadyRequest(
  requests: readonly CitationRequest[]
): CitationRequest | undefined {
  const reviewable = requests.filter((request) => isReviewableStatus(request.status));

  if (reviewable.length === 0) {
    return undefined;
  }

  const awaitingReview = reviewable.filter((request) => request.status === 'awaiting_review');

  const pool = awaitingReview.length > 0 ? awaitingReview : reviewable;

  // `updatedAt` is the API's zero-padded UTC ISO 8601 timestamp, which
  // sorts correctly under lexicographic string comparison.
  return pool.reduce((latest, request) =>
    request.updatedAt > latest.updatedAt ? request : latest
  );
}

/**
 * Validate-and-narrow an incoming `source` query-string value to the
 * known channel set. Anything outside the allowlist (malformed URL,
 * tampered link, future channel we haven't recognized yet) collapses
 * to `undefined` so we don't pollute analytics with arbitrary strings.
 */
function parseReferencesReadySource(
  raw: string | undefined
): ReferencesReadyDeepLinkSource | undefined {
  return (REFERENCES_READY_SOURCES as readonly string[]).includes(raw ?? '')
    ? (raw as ReferencesReadyDeepLinkSource)
    : undefined;
}

interface UseAutoOpenReferencesReadyInput {
  /** Twilio conversation SID for the chat the user just deep-linked into. */
  conversationId: string;
  /** Value of the `action` search param on the current route. */
  action: string;
  /**
   * Value of the `source` search param on the current route. Used to
   * attribute the analytics event back to the channel (email / sms /
   * push) that originated the tap. Optional — older notifications and
   * non-deep-link entry points won't carry it.
   */
  source?: string;
}

/**
 * Auto-opens the reference-request wizard at the `review` phase when the
 * chat is opened via the `referencesReady` notification deep link.
 *
 * Picks the conversation's reviewable citation request (see
 * `pickReferencesReadyRequest` — `awaiting_review` preferred, else the
 * most recent reviewable request, `published` included) and opens the
 * same surface the bubble pill opens via `openCitationReviewWizard`:
 * the inline modal on web, the `citation-wizard` full-screen route on
 * native. Native needs the parent message's extracted statements, which
 * it reads from the conversation's statement references, so it re-fires
 * once they arrive.
 *
 * Idempotent per citation request via a fired-ref guard, and clears the
 * `action` / `source` params via `router.setParams` after opening so
 * refresh and back-nav don't re-fire, while still letting a later
 * notification into a different conversation re-trigger while the chat
 * stays mounted (desktop web split view).
 */
export function useAutoOpenReferencesReady({
  conversationId,
  action,
  source,
}: UseAutoOpenReferencesReadyInput): void {
  const isReferencesReadyAction = action === REFERENCES_READY_ACTION;

  const { data: citationRequests } = useCitationRequests(conversationId, {
    enabled: isReferencesReadyAction,
  });

  const reviewableRequest = useMemo(
    () => (citationRequests ? pickReferencesReadyRequest(citationRequests) : undefined),
    [citationRequests]
  );

  const targetState = useStatementReferenceState(conversationId, reviewableRequest?.messageSid, {
    enabled: isReferencesReadyAction,
  });

  /** Citation request id we've already opened for, so cache updates /
   * re-renders can't re-fire the wizard before the param clear
   * propagates. */
  const openedForRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isReferencesReadyAction || !reviewableRequest) {
      return;
    }

    if (openedForRequestIdRef.current === reviewableRequest.id) {
      return;
    }

    // The native wizard route needs the message's extracted statements
    // (it renders nothing without them). When they haven't loaded yet,
    // `openCitationReviewWizard` returns false on native; we leave the
    // guard unset and the params intact so the query re-fires this effect
    // once they arrive. Web only needs the message sid — the helper flips
    // the inline triage store and the mounted sheet resyncs from the
    // bubble's live state — so the empty fallbacks below are harmless
    // there.
    const opened = openCitationReviewWizard({
      conversationId,
      messageSid: reviewableRequest.messageSid,
      extractedStatements: targetState?.extractedStatements ?? [],
      statementCitationStatuses: targetState?.statementCitationStatuses,
      attachedStatementReferences: targetState?.attachedStatementReferences ?? [],
      statementReferenceHelpRequests: targetState?.statementReferenceHelpRequests,
      citationRequest: reviewableRequest,
      openSource: 'references_ready_deeplink',
    });

    if (!opened) {
      return;
    }

    openedForRequestIdRef.current = reviewableRequest.id;

    trackReferencesReadyDeepLinkOpened({
      conversationId,
      citationRequestId: reviewableRequest.id,
      messageSid: reviewableRequest.messageSid,
      source: parseReferencesReadySource(source),
    });

    router.setParams({ action: undefined, source: undefined });
  }, [isReferencesReadyAction, reviewableRequest, targetState, conversationId, source]);
}
